import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

export default function ClientActivity() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [activeTab, setActiveTab] = useState('timeline');
  const [activities, setActivities] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Manual activity
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ activity_type: 'meeting', description: '', importance: 'normal' });
  const [saving, setSaving] = useState(false);

  const fetchActivities = () => {
    if (!companyId || !clientId) return;
    setLoading(true);
    const params = new URLSearchParams({ page, per_page: 20 });
    if (search) params.append('search', search);
    if (typeFilter) params.append('activity_type', typeFilter);

    fetch(`${API}/companies/${companyId}/clients/${clientId}/activities?${params}`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => {
        setActivities(Array.isArray(d.data) ? d.data : []);
        setTotalPages(d.last_page || 1);
      })
      .catch(() => setActivities([]))
      .finally(() => setLoading(false));
  };

  const fetchAudit = () => {
    if (!companyId || !clientId) return;
    setLoading(true);
    fetch(`${API}/companies/${companyId}/clients/${clientId}/audit?per_page=20&page=${page}`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => {
        setAuditLogs(Array.isArray(d.data) ? d.data : []);
        setTotalPages(d.last_page || 1);
      })
      .catch(() => setAuditLogs([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (activeTab === 'timeline') fetchActivities();
    else fetchAudit();
  }, [companyId, clientId, page, search, typeFilter, activeTab, API]);

  const handleAddActivity = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/clients/${clientId}/activities`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(addForm),
      });
      if (!res.ok) throw new Error();
      setShowAddModal(false);
      setAddForm({ activity_type: 'meeting', description: '', importance: 'normal' });
      fetchActivities();
    } catch { alert('Failed to add activity'); }
    finally { setSaving(false); }
  };

  const importanceColor = { normal: '#3b82f6', important: '#f59e0b', warning: '#f97316', critical: '#ef4444' };

  if (!companyId) return <div style={pageStyle}><p>Please select a company first.</p></div>;

  return (
    <div style={pageStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        <button onClick={() => navigate(`/company/clients/${clientId}`)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', padding: '4px' }}>←</button>
        <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700 }}>📋 Activity & History</h2>
        {activeTab === 'timeline' && (
          <button onClick={() => setShowAddModal(true)} style={btnPrimary}>+ Add Activity</button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #e2e8f0', marginBottom: '20px' }}>
        {[{ key: 'timeline', label: 'Activity Timeline' }, { key: 'audit', label: 'Audit History' }].map(t => (
          <button
            key={t.key}
            onClick={() => { setActiveTab(t.key); setPage(1); }}
            style={{
              padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: '0.85rem', fontWeight: activeTab === t.key ? 700 : 500,
              color: activeTab === t.key ? '#3b82f6' : '#64748b',
              borderBottom: activeTab === t.key ? '2px solid #3b82f6' : '2px solid transparent',
              marginBottom: '-2px',
            }}
          >{t.label}</button>
        ))}
      </div>

      {activeTab === 'timeline' && (
        <>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <input placeholder="Search..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={inputStyle} />
            <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }} style={selectStyle}>
              <option value="">All Types</option>
              <option value="client_created">Client Created</option>
              <option value="status_changed">Status Changed</option>
              <option value="contact_added">Contact Added</option>
              <option value="service_added">Service Added</option>
              <option value="meeting">Meeting</option>
              <option value="client_merged">Client Merged</option>
            </select>
          </div>

          {initialLoading ? <p>Loading...</p> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {activities.length === 0 ? (
                <div style={{ ...cardStyle, padding: '20px', textAlign: 'center', color: '#94a3b8' }}>No activity found</div>
              ) : activities.map((a, i) => (
                <div key={i} style={{ display: 'flex', gap: '12px', padding: '12px 0', borderBottom: '1px solid #f1f5f9', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '10px', height: '10px', borderRadius: '50%', marginTop: '5px', flexShrink: 0,
                    background: importanceColor[a.importance] || '#3b82f6',
                  }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.85rem', color: '#334155', fontWeight: 500 }}>{a.description}</div>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '4px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <span>{(a.activity_type || '').replace(/_/g, ' ')}</span>
                      <span>{a.user?.name || 'System'}</span>
                      <span>{new Date(a.created_at).toLocaleString()}</span>
                      {a.source_module && <span>{a.source_module.replace(/_/g, ' ')}</span>}
                    </div>
                  </div>
                  <span style={{ ...badgeStyle, background: (importanceColor[a.importance] || '#3b82f6') + '15', color: importanceColor[a.importance] || '#3b82f6' }}>
                    {a.importance}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'audit' && (
        <>
          {initialLoading ? <p>Loading...</p> : (
            <div style={{ ...cardStyle, overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Action</th>
                    <th style={thStyle}>Field</th>
                    <th style={thStyle}>Old Value</th>
                    <th style={thStyle}>New Value</th>
                    <th style={thStyle}>User</th>
                    <th style={thStyle}>Reason</th>
                    <th style={thStyle}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.length === 0 ? (
                    <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No audit records</td></tr>
                  ) : auditLogs.map(a => (
                    <tr key={a.id}>
                      <td style={tdStyle}><span style={{ ...badgeStyle, background: '#e0e7ff', color: '#4f46e5' }}>{a.action}</span></td>
                      <td style={tdStyle}>{a.field_name || '—'}</td>
                      <td style={tdStyle}><span style={{ fontSize: '0.8rem', color: '#ef4444' }}>{a.old_value || '—'}</span></td>
                      <td style={tdStyle}><span style={{ fontSize: '0.8rem', color: '#10b981' }}>{a.new_value || '—'}</span></td>
                      <td style={tdStyle}>{a.user?.name || 'System'}</td>
                      <td style={tdStyle}>{a.reason || '—'}</td>
                      <td style={tdStyle}>{new Date(a.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={paginationBtn}>← Prev</button>
          <span style={{ padding: '6px 12px', fontSize: '0.85rem', color: '#64748b' }}>Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={paginationBtn}>Next →</button>
        </div>
      )}

      {/* Add Activity Modal */}
      {showAddModal && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h3 style={{ margin: '0 0 16px', fontSize: '1.1rem', fontWeight: 700 }}>Add Manual Activity</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={labelStyle}>Activity Type</label>
                <select value={addForm.activity_type} onChange={e => setAddForm({ ...addForm, activity_type: e.target.value })} style={modalInputStyle}>
                  <option value="meeting">Meeting</option>
                  <option value="call">Call</option>
                  <option value="email">Email</option>
                  <option value="escalation">Escalation</option>
                  <option value="relationship_update">Relationship Update</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Description *</label>
                <textarea value={addForm.description} onChange={e => setAddForm({ ...addForm, description: e.target.value })} style={{ ...modalInputStyle, minHeight: '80px' }} placeholder="Describe the activity..." />
              </div>
              <div>
                <label style={labelStyle}>Importance</label>
                <select value={addForm.importance} onChange={e => setAddForm({ ...addForm, importance: e.target.value })} style={modalInputStyle}>
                  <option value="normal">Normal</option>
                  <option value="important">Important</option>
                  <option value="warning">Warning</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowAddModal(false)} style={btnCancel}>Cancel</button>
              <button onClick={handleAddActivity} disabled={saving || !addForm.description} style={btnPrimary}>{saving ? 'Adding...' : 'Add Activity'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const pageStyle = { padding: '24px', maxWidth: '1200px' };
const cardStyle = { background: '#fff', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' };
const thStyle = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' };
const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', color: '#334155' };
const badgeStyle = { padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize' };
const inputStyle = { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem', minWidth: '200px' };
const selectStyle = { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem', background: '#fff' };
const btnPrimary = { padding: '8px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' };
const btnCancel = { padding: '8px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontSize: '0.85rem', cursor: 'pointer' };
const paginationBtn = { padding: '6px 14px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.85rem', cursor: 'pointer' };
const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalStyle = { background: '#fff', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '500px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' };
const labelStyle = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '4px' };
const modalInputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem', boxSizing: 'border-box' };
